'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import { Star, BadgeCheck, ArrowRight, Braces } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'
import { ExplainerFrame } from './explainer-frame'

// A review collected by Idukki becomes stars on the product page, then
// stars under the product in Google. The middle panel is the part most
// review tools skip: the structured data has to be on the merchant's
// own page for Google to associate it with the product.

const T = { review: 0.2, pdp: 1.0, serp: 1.9 }

function Stars({ n = 5, className = 'h-3 w-3' }: { n?: number; className?: string }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`${className} ${i < n ? 'fill-amber-400 text-amber-400' : 'text-border'}`} aria-hidden />
      ))}
    </span>
  )
}

export function ReviewsToGoogleExplainer({ className }: { className?: string }) {
  return (
    <ExplainerFrame
      eyebrow="Reviews and Google"
      title="From a review to a rich result"
      caption="Collect the review, publish it with Product and AggregateRating structured data on your own PDP, and Google can show the stars under your listing."
      className={className}
    >
      {({ play, reduce, d }) => (
        <LazyMotion features={domAnimation}>
          <div className="grid items-center gap-3 sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
            {/* 1. Collected review */}
            <m.div
              className="rounded-2xl border bg-card p-3.5"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={play ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(T.review) }}
            >
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-full bg-primary/20" aria-hidden />
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-foreground">Sample reviewer</p>
                  <p className="flex items-center gap-1 text-[10px] text-emerald-700"><BadgeCheck className="h-3 w-3" aria-hidden /> Verified buyer</p>
                </div>
              </div>
              <div className="mt-2"><Stars /></div>
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">Strap fits small wrists, face is clean and readable. Wore it every day for a month.</p>
              <span className="mt-2 block h-12 w-full rounded-lg bg-gradient-to-br from-secondary to-primary/20" aria-hidden />
            </m.div>

            <Arrow play={play} reduce={reduce} delay={d(T.pdp - 0.2)} />

            {/* 2. PDP with schema */}
            <m.div
              className="rounded-2xl border bg-card p-3.5"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={play ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(T.pdp) }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Your product page</p>
              <p className="mt-1 text-xs font-semibold text-foreground">Silver-Toned Watch</p>
              <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Stars /> 4.8 · 213 reviews</p>
              <m.p
                className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 font-mono text-[10px] text-foreground"
                initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                animate={play ? { opacity: 1, scale: 1 } : undefined}
                transition={{ ...SPRING, delay: d(T.pdp + 0.35) }}
              >
                <Braces className="h-3 w-3 text-primary" aria-hidden /> AggregateRating 4.8 / 213
              </m.p>
            </m.div>

            <Arrow play={play} reduce={reduce} delay={d(T.serp - 0.2)} />

            {/* 3. Google result */}
            <m.div
              className="rounded-2xl border bg-card p-3.5"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={play ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(T.serp) }}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Google search</p>
              <p className="mt-1 truncate text-xs font-medium text-primary underline-offset-2">Silver-Toned Watch · yourstore.com</p>
              <m.p
                className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground"
                initial={reduce ? false : { opacity: 0 }}
                animate={play ? { opacity: 1 } : undefined}
                transition={{ duration: 0.3, delay: d(T.serp + 0.4) }}
              >
                <Stars className="h-2.5 w-2.5" /> Rating: 4.8 · 213 reviews · $24.76 · In stock
              </m.p>
              <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">Strap fits small wrists, face is clean and readable…</p>
            </m.div>
          </div>
        </LazyMotion>
      )}
    </ExplainerFrame>
  )
}

function Arrow({ play, reduce, delay }: { play: boolean; reduce: boolean; delay: number }) {
  return (
    <m.span
      className="hidden justify-center text-muted-foreground sm:flex"
      initial={reduce ? false : { opacity: 0, x: -6 }}
      animate={play ? { opacity: 1, x: 0 } : undefined}
      transition={{ duration: 0.3, ease: EASE, delay }}
      aria-hidden
    >
      <ArrowRight className="h-4 w-4" />
    </m.span>
  )
}
